import {
    BotType,
    Currency,
    DrivingLicence,
    EmailSendingLogUiEmailTypeEnum,
    EventType, FinanceProvider,
    Integration,
    LocaleTag,
    NewVehicleDataConfigurationDTOVehicleTypeEnum,
    ResidentialStatus,
    VehicleCondition,
    VehicleDataProvider,
    VehicleServiceHistory
} from "../api/generated";
import {BuiltInKnowledgeType} from "../components/modules/assistant/franchiseTypes";
import {KEYLOOP_PIPELINE_SEARCH_PROVIDER} from "../components/modules/location/types/pipelineSearch";
const ChatOutcomeEnumMap: Partial<Record<EventType, string>> = {
    [EventType.ReturnedUsedVehicleInformation]: "Used Vehicle Information",
    [EventType.ReturnedNewVehicleInformation]: "New Vehicle Information",
    [EventType.ReturnedVehicleValuation]: "Vehicle Valued",
    [EventType.ReservedVehicle]: "Vehicle Reserved",
    [EventType.ReturnedFinanceQuote]: "Finance Quote",
    [EventType.ReturnedFinanceApplication]: "Finance Application",
    [EventType.RequestedLimitedVehicleInformation]: "Information Requested",
    [EventType.RequestedLimitedVehicleValuation]: "Valuation Requested",
    [EventType.RequestedLimitedFinanceIllustration]: "Illustration Requested",
    [EventType.RequestedLimitedVehicleReservation]: "Reservation Requested",
};

const FollowUpEnumMap: Partial<Record<EventType, string>> = {
    [EventType.RequestedCallback]: "Callback Requested",
    [EventType.RequestedTestDrive]: "Test Drive Requested",
    [EventType.RequestedEmailFollowUp]: "Email Follow-up Requested",
    [EventType.RequestedServiceCallback]: "Service Callback Requested",
    [EventType.BookedCallBack]: "Callback",
    [EventType.BookedEmailFollowUp]: "Email",
    [EventType.BookedTestDrive]: "Test Drive",
    [EventType.BookedServiceCallBack]: "Service Callback",
};

const EventTypeMap: Partial<Record<EventType, string>> = {
    ...ChatOutcomeEnumMap,
    ...FollowUpEnumMap,
    [EventType.ReturnedLocationInformation]: "Returned Location Information",
    [EventType.ReturnedCompanyInformation]: "Returned Company Information",
    [EventType.ReturnedCompanyWorkingHours]: "Returned Company Working Hours",
    [EventType.ReturnedJourneyDetails]: "Returned Journey Details",
    [EventType.CheckedTestDriveBookingAvailability]: "Test Drive Availability Check",
    [EventType.CheckedCallBackBookingAvailability]: "Callback Availability Check",
    [EventType.ReturnedStripeForm]: "Returned Stripe Form",
    [EventType.AgentHandover]: "Agent Handover",
    [EventType.HumanHandover]: "Human Handover",
    [EventType.ReturnedAftersalesInformation]: "Returned Aftersales Information",
    [EventType.ReturnedAftersalesWorkingHours]: "Returned Aftersales Working Hours",
    [EventType.ReturnedServiceBookingUrl]: "Returned Service Appointment Url",
    [EventType.ReturnedRecallUrl]: "Returned Recall Url",
    [EventType.ReturnedFuelCost]: "Returned Fuel Cost",
};

const VehicleConditionMap: Partial<Record<VehicleCondition, string>> = {
    [VehicleCondition.Excellent]: "Excellent",
    [VehicleCondition.Good]: "Good",
    [VehicleCondition.Average]: "Average",
    [VehicleCondition.BelowAverage]: "Below Average",
    [VehicleCondition.Poor]: "Poor",
};

const VehicleServiceHistoryMap: Partial<Record<VehicleServiceHistory, string>> = {
    [VehicleServiceHistory.FullHistory]: "Full History",
    [VehicleServiceHistory.PartHistory]: "Part History",
    [VehicleServiceHistory.NoHistory]: "No History",
};

const ResidentialStatusMap: Partial<Record<ResidentialStatus, string>> = {
    [ResidentialStatus.CouncilTenant]: "Council Tenant",
    [ResidentialStatus.LivingWithParents]: "Living with Parents",
    [ResidentialStatus.Owner]: "Owner",
    [ResidentialStatus.Tenant]: "Tenant",
};

const DrivingLicenceMap: Partial<Record<DrivingLicence, string>> = {
    [DrivingLicence.Full]: "Full",
    [DrivingLicence.None]: "None",
    [DrivingLicence.Provisional]: "Provisional",
};

const CurrencyMap: Partial<Record<Currency, string>> = {
    [Currency.Gbp]: "£",
};

const IntegrationMap: Partial<Record<Integration, string>> = {
    [Integration.Superchat]: "Superchat",
};

const EmailSendingTypeMap: Partial<Record<EmailSendingLogUiEmailTypeEnum, string>> = {
    [EmailSendingLogUiEmailTypeEnum.TranscriptDefault]: "Transcript (default)",
    [EmailSendingLogUiEmailTypeEnum.TranscriptRouting]: "Transcript (routing)",
    [EmailSendingLogUiEmailTypeEnum.TranscriptAftersales]: "Transcript (aftersales)",
    [EmailSendingLogUiEmailTypeEnum.ImmediateMessage]: "Immediate message"
};

const BotTypeMap: Partial<Record<BotType, string>> = {
    [BotType.AimeStarter]: "AIME Starter",
    [BotType.AimeStock]: "AIME Stock",
    [BotType.AimePx]: "AIME PX",
    [BotType.AimeFinance]: "AIME Finance",
    [BotType.AimeReservation]: "AIME Reservation",
    [BotType.TmcAime]: "TMC AIME",
    [BotType.AimeBuy]: "AIME Buy"
};

const KnowledgeTypeMap: Partial<Record<BuiltInKnowledgeType, string>> = {
    [BuiltInKnowledgeType.WARRANTY]: "Warranty",
    [BuiltInKnowledgeType.FINANCE]: "Finance",
    [BuiltInKnowledgeType.PART_EXCHANGE]: "Part Exchange",
    [BuiltInKnowledgeType.SERVICE_AFTERSALES]: "Service Aftersales",
    [BuiltInKnowledgeType.GENERAL_INFORMATION]: "General Information",
    [BuiltInKnowledgeType.MOTABILITY]: "Motability",
    [BuiltInKnowledgeType.COMPLAINTS_AND_COMPLIANCE]: "Complaints and Compliance"
};

const VehicleDataProviderMap: Partial<Record<VehicleDataProvider, string>> = {
    [VehicleDataProvider.NoProvider]: "No Provider",
    [VehicleDataProvider.Autotrader]: "Autotrader",
    [VehicleDataProvider.TmcAutotrader]: "TMC Autotrader",
    [VehicleDataProvider.FileOnSftp]: "File on SFTP",
    [VehicleDataProvider.PipelineStockFile]: "Pipeline Stock File",
    [VehicleDataProvider.SpidersnetFile]: "Spidersnet File",
    [VehicleDataProvider.AutotraderAndFileOnSftp]: "Autotrader and File on SFTP",
    [KEYLOOP_PIPELINE_SEARCH_PROVIDER]: "Keyloop Pipeline Search"
};

const FinanceProviderMap: Partial<Record<FinanceProvider, string>> = {
    [FinanceProvider.KeyloopFinanceService]: "Keyloop Finance Service",
    [FinanceProvider.Codeweavers]: "CodeWeavers",
    [FinanceProvider.Ivendi]: "iVendi",
    [FinanceProvider.Illustration]: "Illustration"
};

const LocaleTagMap: Partial<Record<LocaleTag, string>> = {
    [LocaleTag.EnGb]: "English (UK) - en-GB",
    [LocaleTag.EnUs]: "English (US) - en-US",
    [LocaleTag.EnCa]: "English (Canada) - en-CA",
    [LocaleTag.FrFr]: "French (France) - fr-FR",
    [LocaleTag.FrCa]: "French (Canada) - fr-CA",
    [LocaleTag.DeDe]: "German (Germany) - de-DE",
    [LocaleTag.EsEs]: "Spanish (Spain) - es-ES",
    [LocaleTag.JaJp]: "Japanese (Japan) - ja-JP",
    [LocaleTag.ArAe]: "Arabic (UAE) - ar-AE"
};

const VehicleTypeMap: Partial<Record<NewVehicleDataConfigurationDTOVehicleTypeEnum, string>> = {
    [NewVehicleDataConfigurationDTOVehicleTypeEnum.Car]: "Car",
    [NewVehicleDataConfigurationDTOVehicleTypeEnum.Lcv]: "LCV",
    [NewVehicleDataConfigurationDTOVehicleTypeEnum.Bike]: "Bike"
};

const getLabel = (map: Partial<Record<string | number, string>>, value: string | number, defaultValue?: string): string => {
    const label = map[value];
    if (label !== undefined) return label;
    if (defaultValue !== undefined) return defaultValue;
    console.error(`Unsupported value: ${value} for enum map: ${JSON.stringify(map, null, 2)}`);
    return '';
};

const formatFallbackValue = (value?: string): string => {
    if (!value) return '';
    const normalized = value.replace(/[_-]+/g, ' ').trim();
    return normalized.replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
};

export const getChatOutcomeTitle = (value: EventType): string => {
    return getLabel(ChatOutcomeEnumMap, value)
};

export const getFollowUpTitle = (value: EventType): string => {
    return getLabel(FollowUpEnumMap, value)
};

export const getEventTypeTitle = (value: EventType): string => {
    return getLabel(EventTypeMap, value)
};

export const getVehicleConditionTitle = (value?: string): string => {
    if (!value) return '';
    return getLabel(VehicleConditionMap, value, formatFallbackValue(value))
};

export const getVehicleServiceHistoryTitle = (value?: string): string => {
    if (!value) return '';
    return getLabel(VehicleServiceHistoryMap, value, formatFallbackValue(value))
};

export const getResidentialStatusTitle = (value: ResidentialStatus): string => {
    return getLabel(ResidentialStatusMap, value)
};

export const getDrivingLicenceTitle = (value: DrivingLicence): string => {
    return getLabel(DrivingLicenceMap, value)
};

export const getCurrencyTitle = (value: Currency): string => {
    return getLabel(CurrencyMap, value)
};

export const getIntegrationMap = (value: Integration): string => {
    return getLabel(IntegrationMap, value)
};

export const getEmailSendingTypeTitle = (value: EmailSendingLogUiEmailTypeEnum | string): string => {
    return getLabel(EmailSendingTypeMap, value, value)
};

export const getBotTypeTitle = (value: BotType): string => {
    return getLabel(BotTypeMap, value)
};

/**
 * Knowledge types are no longer a closed enum: admins define their own. A built-in type keeps its
 * fixed label; any other key takes the name from the loaded type definitions and, if those have
 * not loaded (or the type was removed), shows the raw key rather than nothing.
 */
export const getKnowledgeTypeTitle = (value: string, knowledgeTypes?: { key: string, name: string }[]): string => {
    const loadedName = knowledgeTypes?.find(type => type.key === value)?.name;
    return getLabel(KnowledgeTypeMap, value, loadedName || value)
};

export const getVehicleDataProviderTitle = (value: VehicleDataProvider): string => {
    return getLabel(VehicleDataProviderMap, value)
};

export const getFinanceProviderTitle = (value: FinanceProvider): string => {
    return getLabel(FinanceProviderMap, value)
};

export const getLocaleTagTitle = (value: LocaleTag): string => {
    return getLabel(LocaleTagMap, value, value)
};

export const getVehicleTypeTitle = (value: NewVehicleDataConfigurationDTOVehicleTypeEnum): string => {
    return getLabel(VehicleTypeMap, value, formatFallbackValue(value))
};

export const getNewVehicleManufacturerTitle = (manufacturer: string, vehicleType: NewVehicleDataConfigurationDTOVehicleTypeEnum): string => {
    return `${manufacturer} (${getVehicleTypeTitle(vehicleType)})`
};
